<template>
  <div class="vendor-platforms">
    <button
      type="button"
      class="vendor-platforms__header"
      @click="toggleExpanded"
    >
      <div class="vendor-platforms__header-main">
        <img
          v-if="vendorLogoSrc"
          :src="vendorLogoSrc"
          :alt="vendorName"
          class="vendor-platforms__vendor-logo"
        >
        <div v-else class="vendor-platforms__vendor-fallback">
          {{ vendorInitials }}
        </div>

        <div class="vendor-platforms__vendor-copy">
          <div class="vendor-platforms__vendor-name">{{ vendorName }}</div>
          <div v-if="vendorDescription" class="vendor-platforms__vendor-description">{{ vendorDescription }}</div>
        </div>
      </div>

      <SVGIcon
        class="vendor-platforms__chevron"
        :iconName="expanded ? 'expand_less' : 'expand_more'"
        :width="'28px'"
        :height="'28px'"
      />
    </button>

    <div v-if="expanded" class="vendor-platforms__body">
      <div class="vendor-platforms__list-div">
        <div v-if="loading" class="vendor-platforms__state">
          Loading platforms...
        </div>

        <div v-else-if="errorMessage" class="vendor-platforms__state vendor-platforms__state--error">
          {{ errorMessage }}
        </div>

        <SelectedPlatformsList
          v-else
          :items="platformItems"
          :draggableCards="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';
import SelectedPlatformsList from '@/components/Platforms/SelectedPlatformsList.vue';

const props = defineProps({
  vendor: {
    type: Object,
    default: () => ({}),
  },
  expanded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle']);

const loading = ref(false);
const errorMessage = ref('');
const platforms = ref([]);
const hasLoaded = ref(false);
const dataConnect = new DatastoreConnect();

const vendorName = computed(() => props.vendor?.name || props.vendor?.code || 'Vendor');
const vendorDescription = computed(() => props.vendor?.description || '');
const vendorId = computed(() => Number(props.vendor?.id || 0));

const vendorInitials = computed(() => (
  String(vendorName.value || 'V')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
));

const vendorLogoSrc = computed(() => resolveImageSource(props.vendor, ['logo', 'logo_url', 'logoUrl', 'image', 'image_url', 'imageUrl']));

const platformItems = computed(() => (
  (platforms.value || []).map((platform) => ({
    vendor: props.vendor,
    platform,
  }))
));

async function toggleExpanded() {
  emit('toggle', props.vendor || {});
}

watch(() => props.expanded, async (isExpanded) => {
  if (!isExpanded || hasLoaded.value || loading.value) {
    return;
  }

  await loadPlatforms();
}, { immediate: true });

async function loadPlatforms() {
  if (!vendorId.value) {
    errorMessage.value = 'Missing vendor id';
    return;
  }

  loading.value = true;
  errorMessage.value = '';

  try {
    const response = await dataConnect.api('platform.list', {
      vendor_id: vendorId.value,
    });

    if (response?.error) {
      throw new Error(response.error.message || 'Failed to load platforms');
    }

    platforms.value = Array.isArray(response?.result) ? response.result : [];
    hasLoaded.value = true;
  } catch (error) {
    errorMessage.value = String(error?.message || error || 'Failed to load platforms');
  } finally {
    loading.value = false;
  }
}

function resolveImageSource(source, keys) {
  if (!source || typeof source !== 'object') {
    return '';
  }

  for (const key of keys) {
    const value = source[key];
    const normalized = normalizeImageValue(value);
    if (normalized) {
      return normalized;
    }
  }

  return '';
}

function normalizeImageValue(value) {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    if (trimmed.startsWith('data:image') || trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
      return trimmed;
    }
    return `data:image/png;base64,${trimmed}`;
  }

  if (Array.isArray(value) && value.length > 0) {
    return bytesToDataUrl(new Uint8Array(value));
  }

  if (value instanceof Uint8Array) {
    return bytesToDataUrl(value);
  }

  return '';
}

function bytesToDataUrl(bytes) {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return `data:image/png;base64,${btoa(binary)}`;
}
</script>

<style lang="scss" scoped>
.vendor-platforms {
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.16), 0 0 1px 1px rgba(0, 0, 0, 0.06);
  @include themify() {
    background: themed('card-color');
    border: 1px solid themed('color-primary-200');
  }
}

.vendor-platforms__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 10px 18px 6px;
  text-align: left;
  border: none;
  cursor: pointer;
  @include themify() {
    background: themed('card-color');
    color: themed('body-text-color');
  }
}

.vendor-platforms__header-main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.vendor-platforms__vendor-logo {
  width: 72px;
  height: 48px;
  object-fit: contain;
  flex: 0 0 auto;
}

.vendor-platforms__vendor-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  font-size: 1rem;
  font-weight: 700;
  flex: 0 0 auto;
  @include themify() {
    color: themed('color-white-100');
    background: themed('color-primary');
  }
}

.vendor-platforms__vendor-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.vendor-platforms__vendor-name {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.2;
}

.vendor-platforms__vendor-description {
  margin-top: 4px;
  font-size: 0.9rem;
  line-height: 1.35;
  @include themify() {
    color: themed('body-text-color-secondary');
  }
}

.vendor-platforms__chevron {
  flex: 0 0 auto;
  @include themify() {
    color: themed('color-primary');
  }
}

.vendor-platforms__body {
  padding: 0;
}

.vendor-platforms__list-div {
  padding: 2px;
  @include themify() {
    background: #d1d9e6;
  }
}

.vendor-platforms__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  border-radius: 10px;
  @include themify() {
    color: themed('body-text-color-secondary');
    background: #e4e8ef;
  }
}

.vendor-platforms__state--error {
  @include themify() {
    color: themed('color-status-error');
  }
}
</style>
